import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { useAuth } from '@/hooks/useAuth'
import { agreementPlatform, useAcceptAgreement } from '@/lib/hooks/agreement'

interface AgreementGateProps {
  version: string
  documents: ReadonlyArray<{ title: string; url: string }>
}

// Blocks the app until the signed-in user accepts the current user agreement
// with one mandatory checkbox. Shown on first use (including invited crew who
// never saw the web signup) and again whenever the agreement version changes.
export function AgreementGate({ version, documents }: AgreementGateProps) {
  const { signOut } = useAuth()
  const [agreed, setAgreed] = useState(false)
  const accept = useAcceptAgreement()

  return (
    <div className="flex-1 bg-surface-L0 flex items-center justify-center p-4 pt-safe pb-safe">
      <div className="flex flex-col gap-6 max-w-[361px] w-full">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold text-content-primary-bold">User agreement</h1>
          <p className="text-sm text-content-primary-subtle">
            Please review and accept the Pronteer user agreement to continue.
          </p>
        </div>

        <ul className="flex flex-col gap-2">
          {documents.map((doc) => (
            <li key={doc.url}>
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-content-brand-bold underline"
              >
                {doc.title}
              </a>
            </li>
          ))}
        </ul>

        <label className="flex items-start gap-3 text-sm text-content-primary-bold">
          <Checkbox
            checked={agreed}
            onCheckedChange={(checked) => setAgreed(checked === true)}
            disabled={accept.isPending}
            className="mt-0.5"
          />
          <span>I have read and agree to the Pronteer user agreement above.</span>
        </label>

        {accept.error && (
          <p className="text-xs font-semibold text-content-negative-bold">{accept.error.message}</p>
        )}

        <div className="flex flex-col gap-2">
          <Button
            variant="brand"
            className="w-full"
            disabled={!agreed}
            loading={accept.isPending}
            onClick={() => accept.mutate({ version, platform: agreementPlatform() })}
          >
            Continue
          </Button>
          <Button
            variant="secondary"
            className="w-full"
            disabled={accept.isPending}
            onClick={() => signOut()}
          >
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  )
}
